import React from 'react';
import styled from 'styled-components';
import { Modal } from './Modal';

// The dialog contract, in one place: the Modal props a dialog wants, the role/aria-modal/aria-label
// trio that makes it a dialog to assistive tech, and the button row every one of them ends with.
//
// `const MODAL_PROPS = { maximizeWidth: false, forceHeight: false }` was declared identically in
// three files — ConfirmModal, DrillDownUnavailableModal, CompanyScoreHistory — each beside its own
// Dialog div carrying the same three aria attributes and its own flex-end ButtonRow. They had
// already drifted: ConfirmModal's row gaps at 12px, the other two at 8px. A consistent dialog
// treatment — a close affordance, an entry animation, a standard width ramp — was three edits in
// three files with no shared surface, and they would drift again the moment one was touched.
//
// WHAT THIS DELIBERATELY DOES NOT DO: Escape, focus trap, focus restore. Modal has none of them
// today; CompanyScoreHistory adds a document-level keydown listener of its own and says in place
// that changing dismissal for every modal in the app is not that feature's call to make. It is not
// this commit's either — putting Escape here would silently give it to every dialog in the product.
// That is a deliberate decision with its own commit, and this is the seam that makes it a one-line
// change when someone makes it.

// Sized by the caller. Without a maxWidth the dialog keeps sizing to its content, which is what
// every existing caller expects and what Modal does when maximizeWidth is false.
const Panel = styled.div`
  ${({ $maxWidth }) => ($maxWidth ? `max-width: ${$maxWidth};` : '')}
  ${({ $width }) => ($width ? `width: ${$width};` : '')}
`;

// Actions, right-aligned. 12px rather than 8px: the two values were an accident of three separate
// authorings, and the wider one is the house spacing step.
export const DialogButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export const MODAL_PROPS = { maximizeWidth: false, forceHeight: false };

// `label` is the dialog's accessible name and is required — a dialog without one announces itself
// as an unnamed group, which is worse than not being a dialog at all. Both feed dialogs' tests
// assert on it, so it is a contract rather than a nicety.
export const Dialog = ({ label, onDismiss, maxWidth, width, children }) => (
  <Modal onDismiss={onDismiss} modalProps={MODAL_PROPS}>
    <Panel role="dialog" aria-modal="true" aria-label={label} $maxWidth={maxWidth} $width={width}>
      {children}
    </Panel>
  </Modal>
);
